import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subject, firstValueFrom, of } from 'rxjs';
import { catchError, map, startWith, switchMap, tap } from 'rxjs/operators';
import { EnfafModalComponent } from '../../../../../components/enfaf-modal/enfaf-modal.component';
import { EnfafPaginationComponent, PageChangeEvent, PaginationConfig } from '../../../../../components/enfaf-pagination/enfaf-pagination.component';
import { EnfafSearchableInputComponent } from '../../../../../components/enfaf-searchable-input/enfaf-searchable-input.component';
import { ApiService } from '../../../../../core/services/api/api.service';
import { ToastService } from '../../../../../core/services/toast/toast.service';
import { LinkErrorCode, Paginated, PurchaseRow, UnlinkedPayment } from '../../../../../models/interfaces/god-orders.interface';
import { apiErrorMessage } from '../../load-error';
import { formatEuros, formatShortDate } from '../../purchase-format';
import { createSearchBox } from '../../search-box';
import { PaymentMethodCellComponent } from '../payment-method-cell/payment-method-cell.component';

export interface ModalLinkPaymentData {
  /** Purchase the admin is linking a payment to. */
  purchase: PurchaseRow;
}

const PAGE_SIZE = 10;
/** Errors after which the offered payments are stale (another admin or a webhook moved it first). */
const STALE_LIST_CODES: LinkErrorCode[] = ['ALREADY_GROUPED', 'ALREADY_IN_PURCHASE', 'PAYMENT_CHANGED', 'RECURRING_SUBSCRIPTION'];

/**
 * "Vincular pago" (B.4): search box and paginated list of the unlinked payments of the purchase's own student (D97: the
 * API lists only those and answers 422 `OTHER_STUDENT`, with no way to confirm, to any other). Closes with the updated
 * `PurchaseRow`.
 */
@Component({
  selector: 'app-modal-link-payment',
  imports: [EnfafModalComponent, EnfafPaginationComponent, EnfafSearchableInputComponent, PaymentMethodCellComponent, TranslatePipe],
  templateUrl: './modal-link-payment.component.html',
  styleUrl: './modal-link-payment.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ModalLinkPaymentComponent {
  protected readonly data = inject<ModalLinkPaymentData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject<MatDialogRef<ModalLinkPaymentComponent, PurchaseRow | undefined>>(MatDialogRef);
  private readonly api = inject(ApiService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly reload$ = new Subject<void>();

  protected readonly search = signal('');
  protected readonly page = signal(1);
  protected readonly response = signal<Paginated<UnlinkedPayment> | null>(null);
  protected readonly loading = signal(true);
  protected readonly loadFailed = signal(false);
  protected readonly selectedId = signal<number | null>(null);
  protected readonly saving = signal(false);
  /** Option that holds the single tab stop of the list (roving tabindex); null = the picked one, or the first. */
  protected readonly focusedIndex = signal<number | null>(null);
  private readonly list = viewChild<ElementRef<HTMLElement>>('list');
  /** Why the last link was refused, shown inside the modal (422 `OTHER_STUDENT`, D97). */
  protected readonly linkError = signal<string | null>(null);
  protected readonly searchControl = createSearchBox(
    () => this.search(),
    (search) => {
      this.search.set(search);
      this.focusedIndex.set(null);
      this.page.set(1);
      this.reload$.next();
    }
  );

  protected readonly formatEuros = formatEuros;
  protected readonly formatShortDate = formatShortDate;

  protected readonly paginationConfig = (): PaginationConfig => ({
    currentPage: this.page(),
    totalItems: this.response()?.total ?? 0,
    itemsPerPage: PAGE_SIZE,
    maxVisiblePages: 5,
    showPageSize: false,
    showResultsCount: false
  });

  constructor() {
    this.reload$
      .pipe(
        startWith(undefined),
        tap(() => {
          this.loading.set(true);
          this.loadFailed.set(false);
        }),
        // switchMap drops the answer of a superseded request (typing fast).
        switchMap(() =>
          this.api
            .getGodUnlinkedPayments({
              purchaseId: this.data.purchase.purchaseId,
              search: this.search() || undefined,
              page: this.page(),
              pageSize: PAGE_SIZE
            })
            .pipe(
              map((response) => ({ response, failed: false })),
              catchError(() => of({ response: null, failed: true }))
            )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ response, failed }) => {
        this.loading.set(false);
        this.loadFailed.set(failed);
        if (response) this.response.set(response);
      });
  }

  /** The picked payment, only while it is on the page shown: a search or page change hides it and disables "Confirmar". */
  protected selected(): UnlinkedPayment | null {
    const id = this.selectedId();
    return this.response()?.data.find((payment) => payment.orderId === id) ?? null;
  }

  protected select(payment: UnlinkedPayment, index?: number): void {
    this.selectedId.set(payment.orderId);
    if (index !== undefined) this.focusedIndex.set(index);
    this.linkError.set(null);
  }

  protected activeIndex(payments: UnlinkedPayment[]): number {
    const focused = this.focusedIndex();
    if (focused !== null && focused < payments.length) return focused;
    const picked = payments.findIndex((payment) => payment.orderId === this.selectedId());
    return picked === -1 ? 0 : picked;
  }

  protected onListKeydown(event: KeyboardEvent, payments: UnlinkedPayment[]): void {
    const current = this.activeIndex(payments);
    const last = payments.length - 1;
    const moves: Record<string, number> = { ArrowDown: Math.min(current + 1, last), ArrowUp: Math.max(current - 1, 0), Home: 0, End: last };
    if (event.key in moves) {
      event.preventDefault();
      this.focusOption(moves[event.key]);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.select(payments[current], current);
    }
  }

  private focusOption(index: number): void {
    this.focusedIndex.set(index);
    this.list()?.nativeElement.querySelectorAll<HTMLElement>('[role="option"]')[index]?.focus();
  }

  protected onPageChange(event: PageChangeEvent): void {
    this.page.set(event.currentPage);
    this.focusedIndex.set(null);
    this.reload$.next();
  }

  protected retry(): void {
    this.reload$.next();
  }

  protected close(): void {
    if (!this.saving()) this.dialogRef.close(undefined);
  }

  protected studentName(payment: UnlinkedPayment): string {
    return `${payment.student.name} ${payment.student.lastname}`.trim();
  }

  protected async confirm(): Promise<void> {
    const payment = this.selected();
    if (!payment || this.saving()) return;

    this.saving.set(true);
    this.linkError.set(null);
    try {
      const purchase = await firstValueFrom(this.api.linkGodPurchasePayment(this.data.purchase.purchaseId, { orderId: payment.orderId }));
      this.toast.presentSuccessToast(this.translate.instant('GOD_ORDERS.LINK.SUCCESS'));
      this.dialogRef.close(purchase);
    } catch (error: any) {
      const code: LinkErrorCode | undefined = error?.error?.code;
      if (code === 'OTHER_STUDENT') {
        // Only payments of the same account can be linked (D97): said in the modal, where the admin is choosing.
        this.linkError.set(this.translate.instant('GOD_ORDERS.LINK.ERRORS.OTHER_STUDENT'));
        return;
      }
      const stale = !!code && STALE_LIST_CODES.includes(code);
      this.toast.presentErrorToast(
        stale
          ? this.translate.instant(`GOD_ORDERS.LINK.ERRORS.${code}`)
          : apiErrorMessage(error, this.translate.instant('GOD_ORDERS.LINK.ERRORS.GENERIC'))
      );
      if (stale) {
        this.selectedId.set(null);
        this.reload$.next();
      }
    } finally {
      this.saving.set(false);
    }
  }
}
